import Keycloak from 'keycloak-js';
import axios from './axios';
import { KEYCLOAK_CONFIG } from '../configs/auth.config';

// Initialize Keycloak instance
const keycloakInstance = new Keycloak(KEYCLOAK_CONFIG);
let isInitialized = false;

/**
 * Initializes the Keycloak instance and attempts to authenticate
 * @param {Function} onSuccess - Callback when auth is successful
 * @param {Function} onError - Callback when auth fails
 * @returns {Promise} - Promise that resolves when initialization is complete
 */
const initKeycloak = (onSuccess, onError) => {
  if (isInitialized) {
    return Promise.resolve(keycloakInstance.authenticated);
  }
  isInitialized = true;
  return keycloakInstance
    .init({ onLoad: "login-required", checkLoginIframe: false })
    .then((authenticated) => {
      if (authenticated) {
        updateAxiosHeaders();
        onSuccess && onSuccess(authenticated);
      } else {
        onError && onError();
      }
      return authenticated;
    })
    .catch(error => {
      console.error('Keycloak init error:', error);
      onError && onError(error);
    });
};

/**
 * Sets the authorization header for all Axios requests
 */
const updateAxiosHeaders = () => {
  if (keycloakInstance.token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${keycloakInstance.token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

/**
 * Logs the user in using Keycloak's login page
 */
const doLogin = () => {
  keycloakInstance.login();
};

/**
 * Logs the user out
 */
const doLogout = () => {
  keycloakInstance.logout();
};

/**
 * Refreshes the token if it's valid
 */
const refreshToken = () => {
  return new Promise((resolve, reject) => {
    keycloakInstance.updateToken(5)
      .then(refreshed => {
        if (refreshed) {
          updateAxiosHeaders();
        }
        resolve(refreshed);
      })
      .catch(error => {
        console.error('Failed to refresh token:', error);
        reject(error);
      });
  });
};

/**
 * Checks if the user is authenticated
 * @returns {boolean} - Whether the user is authenticated
 */
const isAuthenticated = () => {
  return !!keycloakInstance.authenticated;
};

/**
 * Gets the user profile from Keycloak
 * @returns {Object} - User profile information
 */
const getUserProfile = () => {
  if (!keycloakInstance.authenticated) return null;
  // Get avatar URL from Keycloak
  const avatarUrl = `${KEYCLOAK_CONFIG.url}/resources/yudm3/admin/henddu/img_avatar.svg`;

  return {
    id: keycloakInstance.subject,
    username: keycloakInstance.tokenParsed.preferred_username,
    email: keycloakInstance.tokenParsed.email,
    firstName: keycloakInstance.tokenParsed.given_name,
    lastName: keycloakInstance.tokenParsed.family_name,
    roles: keycloakInstance.tokenParsed.realm_access?.roles || [],
    picture: keycloakInstance.tokenParsed.picture || avatarUrl,
  };
};

/**
 * Gets the Keycloak token
 * @returns {string} - The current token
 */
const getToken = () => keycloakInstance.token;

/**
 * Gets the Keycloak instance
 * @returns {Keycloak} - The Keycloak instance
 */
const getKeycloakInstance = () => keycloakInstance;

export {
  initKeycloak,
  doLogin,
  doLogout,
  refreshToken,
  isAuthenticated,
  getUserProfile,
  getToken,
  getKeycloakInstance,
  updateAxiosHeaders,
};
